import Swiper from "swiper";
import {
    Pagination,
    Autoplay,
    A11y,
    Controller
} from "swiper/modules";
import { TallestSlideModule } from "../modules/swiper/TallestSlideModule.js";
import type { SwiperOptions } from "swiper/types";


/**
 * Swiper attaches its instance to the initialized element.
 */
type SwiperElement = HTMLElement & {
    swiper?: Swiper;
};


/**
 * Store initialized instances globally.
 *
 * This protects against the same module being loaded or executed
 * more than once.
 */
declare global {
    interface Window {
        __sliderSwiperInstances?: WeakMap<HTMLElement, Swiper>;
    }
}

const swiperInstances =
    window.__sliderSwiperInstances ??=
        new WeakMap<HTMLElement, Swiper>();


/**
 * Helpers to assess autoplay behaviour based on a bitmask.
 */
export enum AutoplayBehaviour {
    PauseOnInteraction = 1,
    PauseOnHover = 2,
    ReverseTheDirection = 4,
    StopAfterFirstIteration = 8
}

export const pauseOnInteraction = (
    behaviour: number
): boolean =>
    (
        behaviour &
        AutoplayBehaviour.PauseOnInteraction
    ) === AutoplayBehaviour.PauseOnInteraction;

export const pauseOnHover = (
    behaviour: number
): boolean =>
    (
        behaviour &
        AutoplayBehaviour.PauseOnHover
    ) === AutoplayBehaviour.PauseOnHover;

export const reverseTheDirection = (
    behaviour: number
): boolean =>
    (
        behaviour &
        AutoplayBehaviour.ReverseTheDirection
    ) === AutoplayBehaviour.ReverseTheDirection;

export const stopAfterFirstIteration = (
    behaviour: number
): boolean =>
    (
        behaviour &
        AutoplayBehaviour.StopAfterFirstIteration
    ) === AutoplayBehaviour.StopAfterFirstIteration;


/**
 * Helpers to assess animation behaviour based on a bitmask.
 */
export enum AnimationBehaviour {
    AnimateInGroups = 1,
    Loop = 2
}

export const animateInGroups = (
    behaviour: number
): boolean =>
    (
        behaviour &
        AnimationBehaviour.AnimateInGroups
    ) === AnimationBehaviour.AnimateInGroups;

export const loop = (
    behaviour: number = 0
): boolean =>
    (
        behaviour &
        AnimationBehaviour.Loop
    ) === AnimationBehaviour.Loop;


/**
 * Helpers to assess navigation options based on a bitmask.
 */
export enum NavigationOption {
    ShowNavigation = 1,
    ShowPagination = 2
}

export const showNavigation = (
    behaviour: number = 0
): boolean =>
    (
        behaviour &
        NavigationOption.ShowNavigation
    ) === NavigationOption.ShowNavigation;

export const showPagination = (
    behaviour: number = 0
): boolean =>
    (
        behaviour &
        NavigationOption.ShowPagination
    ) === NavigationOption.ShowPagination;


/**
 * Implements one slider instance.
 */
export class Slider
{
    /**
     * Constructor.
     */
    public constructor(
        private readonly slider: HTMLElement
    ) {
        this.instantiate();
    }

    /**
     * Find a direct child carrying the requested class.
     *
     * This prevents a parent slider from accidentally selecting
     * an element belonging to a nested slider.
     */
    private getDirectChild(
        className: string
    ): HTMLElement | null {
        for (const child of Array.from(this.slider.children)) {
            if (
                child instanceof HTMLElement &&
                child.classList.contains(className)
            ) {
                return child;
            }
        }

        return null;
    }

    /**
     * Replace an element with a clean clone.
     *
     * Event listeners registered through addEventListener are not
     * copied to the clone. This removes stale navigation listeners.
     */
    private replaceWithCleanClone(
        element: HTMLElement | null
    ): HTMLElement | null {
        if (!element) {
            return null;
        }

        const clone =
            element.cloneNode(true) as HTMLElement;

        element.replaceWith(clone);

        return clone;
    }

    /**
     * Toggle one or more CSS classes on an element.
     */
    private toggleClasses(
        element: HTMLElement,
        classes: string,
        enabled: boolean
    ): void {
        const classNames = classes
            .split(/\s+/)
            .filter(Boolean);

        for (const className of classNames) {
            element.classList.toggle(
                className,
                enabled
            );
        }
    }

    /**
     * Update one custom navigation control.
     */
    private updateNavigationControl(
        element: HTMLElement | null,
        disabled: boolean,
        disabledClasses: string
    ): void {
        if (!element) {
            return;
        }

        /*
         * DOMTokenList.toggle accepts only one class token at a time,
         * so the class string is split by toggleClasses().
         */
        this.toggleClasses(
            element,
            disabledClasses,
            disabled
        );

        element.setAttribute(
            "aria-disabled",
            disabled ? "true" : "false"
        );

        element.tabIndex =
            disabled ? -1 : 0;
    }

    /**
     * Bind custom navigation.
     *
     * Swiper's Navigation module is intentionally not used here.
     * This ensures that one button click calls slideNext() or
     * slidePrev() exactly once.
     */
    private bindNavigation(
        instance: Swiper,
        nextElement: HTMLElement | null,
        previousElement: HTMLElement | null
    ): void {
        const disabledClasses =
            "opacity-30 cursor-default";

        const updateControls = (): void => {
            if (instance.destroyed) {
                return;
            }

            const loopEnabled =
                instance.params.loop === true;

            const previousDisabled =
                instance.isLocked ||
                (
                    !loopEnabled &&
                    instance.isBeginning
                );

            const nextDisabled =
                instance.isLocked ||
                (
                    !loopEnabled &&
                    instance.isEnd
                );

            this.updateNavigationControl(
                previousElement,
                previousDisabled,
                disabledClasses
            );

            this.updateNavigationControl(
                nextElement,
                nextDisabled,
                disabledClasses
            );
        };

        const runNext = (
            event: Event
        ): void => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            if (instance.destroyed) {
                return;
            }

            const loopEnabled =
                instance.params.loop === true;

            if (
                instance.isLocked ||
                instance.animating ||
                (
                    !loopEnabled &&
                    instance.isEnd
                )
            ) {
                return;
            }

            instance.slideNext();
        };

        const runPrevious = (
            event: Event
        ): void => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            if (instance.destroyed) {
                return;
            }

            const loopEnabled =
                instance.params.loop === true;

            if (
                instance.isLocked ||
                instance.animating ||
                (
                    !loopEnabled &&
                    instance.isBeginning
                )
            ) {
                return;
            }

            instance.slidePrev();
        };

        const handleNextKeydown = (
            event: KeyboardEvent
        ): void => {
            if (
                event.key !== "Enter" &&
                event.key !== " "
            ) {
                return;
            }

            runNext(event);
        };

        const handlePreviousKeydown = (
            event: KeyboardEvent
        ): void => {
            if (
                event.key !== "Enter" &&
                event.key !== " "
            ) {
                return;
            }

            runPrevious(event);
        };

        /*
         * Capture mode lets these handlers intercept the event before
         * any older bubbling listener can act on it.
         */
        nextElement?.addEventListener(
            "click",
            runNext,
            {
                capture: true
            }
        );

        previousElement?.addEventListener(
            "click",
            runPrevious,
            {
                capture: true
            }
        );

        nextElement?.addEventListener(
            "keydown",
            handleNextKeydown,
            {
                capture: true
            }
        );

        previousElement?.addEventListener(
            "keydown",
            handlePreviousKeydown,
            {
                capture: true
            }
        );

        instance.on(
            "init",
            updateControls
        );

        instance.on(
            "slideChange",
            updateControls
        );

        instance.on(
            "transitionEnd",
            updateControls
        );

        instance.on(
            "reachBeginning",
            updateControls
        );

        instance.on(
            "reachEnd",
            updateControls
        );

        instance.on(
            "fromEdge",
            updateControls
        );

        instance.on(
            "lock",
            updateControls
        );

        instance.on(
            "unlock",
            updateControls
        );

        instance.on(
            "breakpoint",
            updateControls
        );

        instance.on(
            "resize",
            updateControls
        );

        instance.on(
            "update",
            updateControls
        );
    }

    /**
     * Instantiate the slider.
     */
    protected instantiate(): void
    {
        const swiperElement =
            this.getDirectChild("swiper") as
                SwiperElement | null;

        if (!swiperElement) {
            throw new Error(
                "Missing direct child with `swiper` class"
            );
        }

        /*
         * Prevent duplicate initialization through the global registry.
         */
        const registeredInstance =
            swiperInstances.get(swiperElement);

        if (
            registeredInstance &&
            !registeredInstance.destroyed
        ) {
            return;
        }

        /*
         * Prevent duplicate initialization through Swiper's own
         * element property.
         */
        if (
            swiperElement.swiper &&
            !swiperElement.swiper.destroyed
        ) {
            swiperInstances.set(
                swiperElement,
                swiperElement.swiper
            );

            return;
        }

        const {
            autoplay = "0",
            autoplayBehaviour = "0",
            autoplayTimeout = "5000",

            animationBehaviour = "0",
            animationDuration = "300",
            animationTransition = "slide",

            navigationOptions = "",

            slides = "0",
            slidesMaxWidth = "",
            slidesSpaceBetween = "0"
        } = this.slider.dataset;

        const slideCount =
            Number(slides) || 0;

        const maxSlideWidth =
            Number(slidesMaxWidth) || 0;

        const spaceBetween =
            Number(slidesSpaceBetween) || 0;

        const animationBehaviourValue =
            Number(animationBehaviour) || 0;

        const navigationOptionsValue =
            Number(navigationOptions) || 0;

        const modules:
            NonNullable<SwiperOptions["modules"]> = [
                TallestSlideModule,
                Controller,
                A11y
            ];

        /*
         * Controls belong to the direct `.controls` child of this slider.
         */
        const controlsRoot =
            this.getDirectChild("controls");

        let nextElement =
            controlsRoot?.querySelector<HTMLElement>(
                '[scope="navigation"][template="right"]'
            ) ?? null;

        let previousElement =
            controlsRoot?.querySelector<HTMLElement>(
                '[scope="navigation"][template="left"]'
            ) ?? null;

        const paginationElement =
            controlsRoot?.querySelector<HTMLElement>(
                "[scope=indexation]"
            ) ?? null;

        /*
         * Replace the navigation elements with clean clones before
         * binding our handlers.
         */
        if (
            showNavigation(
                navigationOptionsValue
            )
        ) {
            nextElement =
                this.replaceWithCleanClone(
                    nextElement
                );

            previousElement =
                this.replaceWithCleanClone(
                    previousElement
                );
        }

        const options:
            Partial<SwiperOptions> = {
                modules,

                init: false,

                direction: "horizontal",

                effect:
                    animationTransition as
                        SwiperOptions["effect"],

                loop:
                    loop(
                        animationBehaviourValue
                    ) &&
                    slideCount > 1,

                speed:
                    Number(animationDuration) ||
                    300,

                /*
                 * Default mobile configuration.
                 */
                slidesPerView: 1,
                slidesPerGroup: 1,
                slidesPerGroupSkip: 0,

                spaceBetween,

                /*
                 * Responsive behaviour follows the Swiper container.
                 */
                breakpointsBase: "container",
                breakpoints: {},

                resizeObserver: true,
                observer: true,
                observeParents: true,
                updateOnWindowResize: true,
                watchOverflow: true,
                watchSlidesProgress: true,

                /*
                 * Ignore additional navigation interactions while a
                 * transition is still running.
                 */
                preventInteractionOnTransition: true
            };

        /*
         * Responsive slides.
         *
         * At every breakpoint, slidesPerGroup equals slidesPerView.
         */
        if (
            maxSlideWidth > 0 &&
            slideCount > 1
        ) {
            const breakpoints:
                NonNullable<
                    SwiperOptions["breakpoints"]
                > = {};

            let breakpoint = 0;
            let visibleSlides = 1;

            while (breakpoint < 1920) {
                breakpoint += maxSlideWidth;
                visibleSlides += 1;

                if (
                    visibleSlides >
                    slideCount
                ) {
                    break;
                }

                breakpoints[breakpoint] = {
                    slidesPerView:
                        visibleSlides,

                    slidesPerGroup:
                        visibleSlides,

                    slidesPerGroupSkip: 0,

                    pagination: {
                        enabled:
                            slideCount >
                            visibleSlides
                    }
                };
            }

            options.breakpoints =
                breakpoints;
        }

        /*
         * Pagination.
         */
        if (
            showPagination(
                navigationOptionsValue
            ) &&
            paginationElement
        ) {
            modules.push(Pagination);

            options.pagination = {
                enabled:
                    slideCount > 1,

                el:
                    paginationElement,

                type: "bullets",
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 1,

                renderBullet: (
                    index: number,
                    className: string
                ): string => {
                    const label =
                        window.translations[
                            "slider.goto"
                        ]?.replace(
                            "%s",
                            `${index + 1}`
                        ) ?? "";

                    return `
                        <span
                            tabindex="0"
                            role="button"
                            aria-label="${label}"
                            class="${className}"
                        ></span>
                    `;
                }
            };
        }

        /*
         * Accessibility.
         */
        options.a11y = {
            enabled: true,

            prevSlideMessage:
                window.translations[
                    "slider.previous"
                ],

            nextSlideMessage:
                window.translations[
                    "slider.next"
                ],

            firstSlideMessage:
                window.translations[
                    "slider.first"
                ],

            lastSlideMessage:
                window.translations[
                    "slider.last"
                ],

            paginationBulletMessage:
                window.translations[
                    "slider.goto"
                ]?.replace(
                    "%s",
                    "{{index}}"
                )
        };

        /*
         * Accessibility for the custom navigation controls.
         */
        if (previousElement) {
            previousElement.setAttribute(
                "role",
                "button"
            );

            previousElement.setAttribute(
                "aria-label",
                window.translations[
                    "slider.previous"
                ] ?? "Previous slides"
            );
        }

        if (nextElement) {
            nextElement.setAttribute(
                "role",
                "button"
            );

            nextElement.setAttribute(
                "aria-label",
                window.translations[
                    "slider.next"
                ] ?? "Next slides"
            );
        }

        /*
         * Autoplay.
         */
        if (Number(autoplay)) {
            modules.push(Autoplay);

            const autoplayBehaviourValue =
                Number(
                    autoplayBehaviour
                ) || 0;

            options.autoplay = {
                delay:
                    Number(
                        autoplayTimeout
                    ) || 5000,

                disableOnInteraction:
                    pauseOnInteraction(
                        autoplayBehaviourValue
                    ),

                pauseOnMouseEnter:
                    pauseOnHover(
                        autoplayBehaviourValue
                    ),

                reverseDirection:
                    reverseTheDirection(
                        autoplayBehaviourValue
                    ),

                stopOnLastSlide:
                    stopAfterFirstIteration(
                        autoplayBehaviourValue
                    )
            };
        }

        try {
            const instance =
                new Swiper(
                    swiperElement,
                    options
                );

            swiperInstances.set(
                swiperElement,
                instance
            );

            if (
                showNavigation(
                    navigationOptionsValue
                )
            ) {
                this.bindNavigation(
                    instance,
                    nextElement,
                    previousElement
                );
            }

            instance.init();

            console.debug(
                "Swiper initialized",
                {
                    element:
                        swiperElement,

                    containerWidth:
                        instance.width,

                    windowWidth:
                        window.innerWidth,

                    currentBreakpoint:
                        instance.currentBreakpoint,

                    slidesPerView:
                        instance.params
                            .slidesPerView,

                    slidesPerGroup:
                        instance.params
                            .slidesPerGroup
                }
            );
        }
        catch (error) {
            swiperInstances.delete(
                swiperElement
            );

            throw error;
        }
    }
}
